/**
 * Admin RefundsPage Component
 *
 * Admin page for reviewing and processing refund requests created when
 * paid bookings are cancelled. Approve fires a real Stripe refund (or
 * records a manual reference for Whish/wire payments); reject releases
 * the held host earning.
 */

import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { AdminLayout } from '../layout';
import { useAdminRefunds, useRefundActions, RefundStatus, AdminRefund } from './hooks';

const formatDate = (date: Date): string =>
  date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const TABS: { key: RefundStatus; label: string }[] = [
  { key: 'pending', label: 'Pending' },
  { key: 'processed', label: 'Processed' },
  { key: 'rejected', label: 'Rejected' },
];

export const RefundsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<RefundStatus>('pending');
  const { refunds, stats, isLoading, refetch } = useAdminRefunds(activeTab);
  const { processRefund, isProcessing } = useRefundActions();

  const [selected, setSelected] = useState<AdminRefund | null>(null);
  const [mode, setMode] = useState<'approve' | 'reject' | null>(null);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [manualReference, setManualReference] = useState('');

  const openModal = (refund: AdminRefund, action: 'approve' | 'reject') => {
    setSelected(refund);
    setMode(action);
    setAmount(String(refund.suggestedRefund));
    setReason('');
    setManualReference('');
  };

  const closeModal = () => {
    setSelected(null);
    setMode(null);
  };

  const handleSubmit = async () => {
    if (!selected || !mode) return;
    try {
      if (mode === 'approve') {
        const parsed = parseFloat(amount);
        if (!(parsed > 0)) {
          toast.error('Enter a refund amount greater than zero');
          return;
        }
        if (selected.paymentMethod !== 'stripe' && !manualReference.trim()) {
          toast.error('A manual transfer reference is required for non-Stripe refunds');
          return;
        }
        await processRefund({
          refundRequestId: selected.id,
          action: 'approve',
          amount: parsed,
          manualReference: manualReference.trim() || undefined,
        });
        toast.success(`Refund of $${parsed.toFixed(2)} approved`);
      } else {
        if (!reason.trim()) {
          toast.error('Enter a rejection reason');
          return;
        }
        await processRefund({
          refundRequestId: selected.id,
          action: 'reject',
          reason: reason.trim(),
        });
        toast.success('Refund request rejected');
      }
      closeModal();
      refetch();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to process refund';
      toast.error(message);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Refunds</h1>
          <p className="text-sm text-gray-500 mt-1">
            Review refund requests from cancelled paid bookings
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Pending" value={String(stats.pending)} highlight={stats.pending > 0} />
          <StatCard label="Pending amount" value={`$${stats.pendingAmount.toFixed(2)}`} />
          <StatCard label="Processed" value={String(stats.processed)} />
          <StatCard label="Rejected" value={String(stats.rejected)} />
        </div>

        {/* Tabs */}
        <div className="flex gap-2">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? 'bg-primary-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {tab.label}
              {tab.key === 'pending' && stats.pending > 0 && (
                <span className="ml-2 px-1.5 py-0.5 text-xs rounded-full bg-white/20">
                  {stats.pending}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto" />
              <p className="text-gray-500 mt-2">Loading refund requests...</p>
            </div>
          ) : refunds.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No {activeTab} refund requests
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <Th>Booking</Th>
                    <Th>Guest</Th>
                    <Th>Paid</Th>
                    <Th>Suggested</Th>
                    <Th>Policy</Th>
                    <Th>Method</Th>
                    <Th>Requested</Th>
                    <Th>{activeTab === 'pending' ? 'Actions' : 'Outcome'}</Th>
                  </tr>
                </thead>
                <tbody>
                  {refunds.map((refund) => (
                    <tr key={refund.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="p-4">
                        <p className="text-sm font-medium text-gray-900 max-w-[180px] truncate">
                          {refund.listingTitle}
                        </p>
                        <p className="text-xs text-gray-500">
                          Cancelled by {refund.cancelledBy}
                        </p>
                      </td>
                      <td className="p-4">
                        <p className="text-sm text-gray-900">{refund.guestName}</p>
                        <p className="text-xs text-gray-500 truncate max-w-[160px]">
                          {refund.guestEmail}
                        </p>
                      </td>
                      <td className="p-4 text-sm font-medium">
                        ${refund.amountPaid.toFixed(2)}
                      </td>
                      <td className="p-4">
                        <p className="text-sm font-medium text-gray-900">
                          ${refund.suggestedRefund.toFixed(2)}
                        </p>
                        <p className="text-xs text-gray-500">{refund.refundPercent}%</p>
                      </td>
                      <td className="p-4">
                        <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700 capitalize">
                          {refund.policy}
                        </span>
                      </td>
                      <td className="p-4 text-sm capitalize">{refund.paymentMethod}</td>
                      <td className="p-4 text-sm text-gray-500">
                        {formatDate(refund.createdAt)}
                      </td>
                      <td className="p-4">
                        {refund.status === 'pending' ? (
                          <div className="flex gap-2">
                            <button
                              onClick={() => openModal(refund, 'approve')}
                              className="px-3 py-1.5 bg-primary-500 text-white text-xs font-medium rounded-lg hover:bg-primary-600"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => openModal(refund, 'reject')}
                              className="px-3 py-1.5 border border-gray-300 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50"
                            >
                              Reject
                            </button>
                          </div>
                        ) : refund.status === 'processed' ? (
                          <div>
                            <p className="text-sm font-medium text-green-700">
                              ${refund.approvedAmount?.toFixed(2)} refunded
                            </p>
                            <p className="text-xs text-gray-500 truncate max-w-[140px]">
                              {refund.stripeRefundId || refund.manualRefundReference || ''}
                            </p>
                          </div>
                        ) : (
                          <p className="text-xs text-gray-500 max-w-[160px]">
                            {refund.rejectionReason || 'Rejected'}
                          </p>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Process Modal */}
      {selected && mode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              {mode === 'approve' ? 'Approve refund' : 'Reject refund request'}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              {selected.listingTitle} - {selected.guestName} paid $
              {selected.amountPaid.toFixed(2)} via {selected.paymentMethod}.
              Policy suggests ${selected.suggestedRefund.toFixed(2)} (
              {selected.refundPercent}%).
            </p>
            {selected.refundReason && (
              <p className="text-xs text-gray-400 mb-4">{selected.refundReason}</p>
            )}

            {mode === 'approve' ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Refund amount (USD)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max={selected.amountPaid}
                    step="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                {selected.paymentMethod !== 'stripe' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Manual transfer reference
                    </label>
                    <input
                      type="text"
                      value={manualReference}
                      onChange={(e) => setManualReference(e.target.value)}
                      placeholder="Whish/wire transaction reference"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Send the refund from the Whish/bank account first, then
                      record its reference here.
                    </p>
                  </div>
                )}
                {selected.paymentMethod === 'stripe' && (
                  <p className="text-xs text-gray-500">
                    Approving fires a real Stripe refund immediately.
                  </p>
                )}
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rejection reason (shown to the guest)
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={closeModal}
                disabled={isProcessing}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={isProcessing}
                className={`px-4 py-2 rounded-lg text-white font-medium disabled:opacity-50 ${
                  mode === 'approve'
                    ? 'bg-primary-500 hover:bg-primary-600'
                    : 'bg-red-500 hover:bg-red-600'
                }`}
              >
                {isProcessing
                  ? 'Processing...'
                  : mode === 'approve'
                    ? 'Approve refund'
                    : 'Reject request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

const Th: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <th className="p-4 text-left text-sm font-medium text-gray-600">{children}</th>
);

const StatCard: React.FC<{ label: string; value: string; highlight?: boolean }> = ({
  label,
  value,
  highlight,
}) => (
  <div
    className={`p-4 rounded-xl border ${
      highlight ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-200'
    }`}
  >
    <p className="text-sm text-gray-500">{label}</p>
    <p className="text-xl font-bold text-gray-900 mt-1">{value}</p>
  </div>
);

export default RefundsPage;
